/**
 * Pure promotion engine, shared by the backend (the committed sale and `POST /promotions/preview`)
 * and the frontend (the till pricing a cart while OFFLINE, POS-I78), so both sides apply the SAME
 * rules and an offline total matches the total the server books when the sale syncs.
 *
 * Keep this file dependency-free (no Prisma / React / Nest): it is imported by both packages.
 */

export interface AppliedPromotion {
  promotionId: string;
  promotionName: string;
  discountAmount: number;
}

export interface CartItem {
  productId: string;
  categoryId?: string | null;
  quantity: number;
  lineSubtotal: number; // after item-level discount, before promotion
}

/** A number, a numeric string, or a decimal object (Prisma.Decimal) that prints as a number. */
export type NumLike = number | string | { toString(): string };

/** Subset of Promotion used by the checkout engine (T-214). */
export interface EnginePromotion {
  id: string;
  name: string;
  /** PERCENTAGE | FIXED_AMOUNT | BUY_X_GET_Y | BUNDLE */
  type: string;
  discountValue: NumLike;
  buyQuantity: number | null;
  getQuantity: number | null;
  minOrderValue: NumLike | null;
  maxDiscount: NumLike | null;
  usageLimit: number | null;
  usageCount: number;
  applicableTo: string;
  recurringType: string | null;
  daysOfWeek: string | null;
  timeOfDayStart: string | null;
  timeOfDayEnd: string | null;
  promotionProducts: { productId: string }[];
  promotionCategories: { categoryId: string }[];
  /** ISO start/end of the promotion. The server filters on them when it loads promotions; the till checks them itself offline. */
  startDate?: string | Date;
  endDate?: string | Date;
}

export function money(n: number): number {
  return parseFloat(n.toFixed(2));
}

function parseDaysOfWeek(raw: string | null): number[] | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);
  } catch {
    return null;
  }
}

/**
 * Day / time window. `daysOfWeek` and clock bounds apply even when
 * `recurringType` is unset — a Weekend promo configured for Sat/Sun must
 * not fire on Monday (T-214).
 */
export function promotionIsInWindow(promo: EnginePromotion, now: Date): boolean {
  const allowedDays = parseDaysOfWeek(promo.daysOfWeek);
  if (allowedDays !== null) {
    if (!allowedDays.includes(now.getDay())) return false;
  }
  if (promo.timeOfDayStart && promo.timeOfDayEnd) {
    const hhmm =
      now.getHours().toString().padStart(2, '0') +
      ':' +
      now.getMinutes().toString().padStart(2, '0');
    if (hhmm < promo.timeOfDayStart || hhmm > promo.timeOfDayEnd) return false;
  }
  return true;
}

export function eligibleCartItems(promo: EnginePromotion, items: CartItem[]): CartItem[] {
  if (promo.applicableTo === 'PRODUCTS') {
    const ids = new Set(promo.promotionProducts.map((p) => p.productId));
    return items.filter((item) => ids.has(item.productId));
  }
  if (promo.applicableTo === 'CATEGORIES') {
    const ids = new Set(promo.promotionCategories.map((c) => c.categoryId));
    return items.filter((item) => item.categoryId != null && ids.has(item.categoryId));
  }
  return items;
}

/**
 * Buy-X-Get-Y: free units = floor(qty / buyQty) * getQty, per eligible line.
 * Quantity below buyQty yields zero — a single unit must not trigger Buy-2-Get-1.
 */
export function buyXGetYDiscountAmount(promo: EnginePromotion, items: CartItem[]): number {
  const buyQty = promo.buyQuantity ?? 0;
  const getQty = promo.getQuantity ?? 0;
  if (buyQty < 1 || getQty < 1) return 0;

  let total = 0;
  for (const item of eligibleCartItems(promo, items)) {
    // A group is buy + get units (buy 2 get 1 = groups of 3): the "get" units are free within it.
    const groupSize = buyQty + getQty;
    if (item.quantity < groupSize || item.quantity <= 0) continue;
    const freeItems = Math.min(
      Math.floor(item.quantity / groupSize) * getQty,
      item.quantity,
    );
    if (freeItems <= 0) continue;
    const unitPrice = item.lineSubtotal / item.quantity;
    total += freeItems * unitPrice;
  }
  return money(total);
}

/** Sequential cap so stacked discounts never exceed the pre-tax taxable line. */
export function capStackedDiscounts(
  applied: AppliedPromotion[],
  taxableLine: number,
): AppliedPromotion[] {
  let remaining = Math.max(0, taxableLine);
  const capped: AppliedPromotion[] = [];
  for (const ap of applied) {
    if (remaining <= 0) break;
    const amount = money(Math.min(ap.discountAmount, remaining));
    if (amount <= 0) continue;
    capped.push({ ...ap, discountAmount: amount });
    remaining = money(remaining - amount);
  }
  return capped;
}

/** Discount of one non-BXGY promotion on a base amount. */
export function calculatePromotionDiscount(promo: EnginePromotion, baseAmount: number): number {
  if (baseAmount <= 0) return 0;

  let discount = 0;
  const discountValue = Number(promo.discountValue);

  // POS-I454: a stored value outside the rules (for example 150 percent saved before the save
  // validation existed) must never give more than the base amount, nor a negative discount.
  if (!Number.isFinite(discountValue) || discountValue <= 0) return 0;

  if (promo.type === 'PERCENTAGE') {
    let pct = discountValue;
    if (pct > 100) {
      console.warn(`[promotions] POS-I454 promotion ${promo.id} has percentage ${pct} above 100; clamped to 100`);
      pct = 100;
    }
    discount = (baseAmount * pct) / 100;
    if (promo.maxDiscount) {
      discount = Math.min(discount, Number(promo.maxDiscount));
    }
  } else if (promo.type === 'FIXED_AMOUNT' || promo.type === 'BUNDLE') {
    if (discountValue > baseAmount) {
      console.warn(`[promotions] POS-I454 promotion ${promo.id} amount ${discountValue} above the base ${baseAmount}; clamped`);
    }
    discount = Math.min(discountValue, baseAmount);
  } else if (promo.type === 'BUY_X_GET_Y') {
    // Never treat the taxable line as the discount. Callers must use buyXGetYDiscountAmount.
    return 0;
  }

  return Math.max(0, money(discount));
}

function discountForPromo(promo: EnginePromotion, items: CartItem[], orderSubtotal: number): number {
  if (promo.type === 'BUY_X_GET_Y') {
    return buyXGetYDiscountAmount(promo, items);
  }

  const eligible = eligibleCartItems(promo, items);
  const eligibleSubtotal =
    promo.applicableTo === 'ORDER' || promo.applicableTo === 'ALL'
      ? orderSubtotal
      : eligible.reduce((sum, item) => sum + item.lineSubtotal, 0);

  return calculatePromotionDiscount(promo, eligibleSubtotal);
}

/**
 * Pure checkout evaluation (T-214). Used by auto-apply, coupon, preview and the offline till.
 * Caps the stacked result so discounts cannot exceed `orderSubtotal`.
 */
export function evaluatePromotions(
  promotions: EnginePromotion[],
  items: CartItem[],
  orderSubtotal: number,
  now: Date = new Date(),
): AppliedPromotion[] {
  const applied: AppliedPromotion[] = [];

  for (const promo of promotions) {
    if (promo.usageLimit !== null && promo.usageCount >= promo.usageLimit) {
      continue;
    }
    if (promo.minOrderValue !== null && orderSubtotal < Number(promo.minOrderValue)) {
      continue;
    }
    if (!promotionIsInWindow(promo, now)) {
      continue;
    }

    const discountAmount = discountForPromo(promo, items, orderSubtotal);
    if (discountAmount > 0) {
      applied.push({
        promotionId: promo.id,
        promotionName: promo.name,
        discountAmount: money(discountAmount),
      });
    }
  }

  return capStackedDiscounts(applied, orderSubtotal);
}
